import React from 'react';
import { DEFAULT_SECONDARY_BACKGROUND_COLOR } from '../theme';
import SignInPage from '../pages/SignInPage';
import LayoutCtrl from './LayoutCtrl';
import PeoplePage from '../pages/chair/PeoplePage';
import ConferencesPage from '../pages/ConferencesPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import SignUpPage from '../pages/SignUpPage';
import SendEmailVerificationPage from '../pages/SendEmailVerificationPage';
import AuthCallbackPage from '../pages/AuthCallbackPage';
import ProfilePage from '../pages/ProfilePage';
import HomePage from '../pages/HomePage';
import SignUpOAuthPage from '../pages/SignUpOAuthPage';
import InvitationFormPage from '../pages/InvitationFormPage';
import checkUrl from '../helpers/checkUrls';
import ConferencesCreatePage from '../pages/ConferencesCreatePage';
import AuthLayout from '../layouts/AuthLayout';
import SettingsFormsPage from '../pages/chair/SettingsFormsPage';
import ThemePlayPage from '../pages/ThemePlayPage';
import AllSubmissionsPage from '../pages/chair/AllSubmissionsPage';
import EmailHistoryPage from '../pages/EmailHistoryPage';
import BiddingPage from '../pages/BiddingPage';
import AllReviewersBiddingPage from '../pages/chair/ReviewersBidsPage';
import DiscussionPage from '../pages/DiscussionPage';
import SubmissionDetailPage from '../pages/SubmissionDetailPage';
import SettingsRolesPage from '../pages/chair/SettingsRolesPage';
import SubmissionEditPage from '../pages/SubmissionEditPage';
import SubmissionNewPage from '../pages/SubmissionNewPage';
import RecruitmentPage from '../pages/chair/RecruitmentPage';
import ReviewEditPage from '../pages/ReviewEditPage';
import AssignmentsBulkImportPage from '../pages/chair/AssignmentsBulkImportPage';
import ReviewersToPapersPage from '../pages/chair/ReviewersToPapersPage';
import PapersToReviewersPage from '../pages/chair/PapersToReviewersPage';
import SettingsAnnouncementsPage from '../pages/chair/SettingsAnnouncementsPage';
import SettingsKeywordsPage from '../pages/chair/SettingsKeywordsPage';
import KeywordsPage from '../pages/KeywordsPage';
import SettingsConferencePage from '../pages/chair/SettingsConferencePage';
import SettingsPaperStatusPage from '../pages/chair/SettingsPaperStatusPage';
import SettingsPlanningPage from '../pages/chair/SettingsPlanningPage';
import LogoutPage from '../pages/LogoutPage';
import AllSubmissionsBiddingPage from '../pages/chair/SubmissionsBidsPage';
import { PermissionString } from '../store/permission/types';
import EmailTemplatesPage from '../pages/chair/EmailTemplatesPage';
import ReviewerBidsPage from '../pages/chair/ReviewerBidsPage';
import { MenuItemName } from '../layouts/types';
import SubmissionBidsPage from '../pages/chair/SubmissionBidsPage';
import ReviewersToPapersDetailPage from '../pages/chair/ReviewersToPapersDetailPage';
import PapersToReviewersDetailPage from '../pages/chair/PapersToReviewersDetailPage';
import ReviewersToReviewersPage from '../pages/chair/ReviewersToReviewersPage';
import ReviewersToReviewersDetailPage from '../pages/chair/ReviewersToReviewersDetailPage';
import DashboardPage from '../pages/chair/DashboardPage';
import ConferenceWizardPage from '../pages/chair/ConferenceWizardPage';
import PersonalInfoWizardPage from '../pages/PersonalInfoWizardPage';
import UserProfileWizardPage from '../pages/UserProfileWizardPage';
import DataAPIAccessPage from '../pages/chair/DataAPIAccessPage';
import LandingPage from '../pages/LandingPage/LandingPage';
import NotFoundPage from '../pages/NotFoundPage';
import SubmissionDetailPageReviewer from '../pages/SubmissionDetailPageReviewer';
import BulkEmailReviewersPage from '../pages/BulkEmailReviewersPage';
import BulkEmailSupervisorsPage from '../pages/BulkEmailSupervisorsPage';
import DebugPage from '../pages/DebugPage';

export interface RouteDefinition {
  name: RouteName;
  path: string;
  logged_access: boolean;
  anonymous_access: boolean;
  email_verified_access: boolean;
  personal_info_filled_access: boolean;
  impersonated_access: boolean;
  track_selected_access: boolean;
  permission?: PermissionString;
  fragment: React.ReactNode;
  main_menu_option?: MenuItemName;
  allow_mobile?: boolean;
}

export const getRouteByName = (name: RouteName): RouteDefinition => {
  const route = appRoutes.find((element) => element.name === name);
  if (!route) throw new Error('Route not found. This must not happen');
  return route;
};

export const getRouteByPath = (path: string): RouteDefinition => {
  const route = appRoutes.find((r: RouteDefinition) => checkUrl(r.path, path));
  if (!route) throw new Error('Route not found. This must not happen');
  return route;
};

export const isWizardPage = (routeName: RouteName) => {
  return ['RouteConferenceWizard', 'RoutePersonalInfoWizard', 'RouteUserProfileWizard'].includes(routeName);
};

export type RouteName =
  | 'RouteRoot'
  | 'RouteNotFound'
  | 'RouteConferences'
  | 'RouteConferenceHome'
  | 'RouteConferencesCreate'
  | 'RoutePeople'
  | 'RouteProfile'
  | 'RouteSettingsForms'
  | 'RouteSettingsRelationsRoles'
  | 'RouteSettingsRoles'
  | 'RouteSettingsKeywords'
  | 'RouteSettingsPlanning'
  | 'RouteAllSubmissions'
  | 'RouteDetailSubmission'
  | 'RouteEditSubmission'
  | 'RouteNewSubmission'
  | 'RouteLogin'
  | 'RouteLogout'
  | 'RouteSignUp'
  | 'RouteSignUpOAuth'
  | 'RouteForgotPassword'
  | 'RouteResetPasswordPage'
  | 'RouteSendEmailVerificationPage'
  | 'RouteAuthCallbackPage'
  | 'RouteInvitationFormPage'
  | 'RouteBidding'
  | 'RouteAllReviewersBids'
  | 'RouteAllSubmissionsBids'
  | 'RouteEmailTemplatesPage'
  | 'RouteEmailHistoryPage'
  | 'RouteRecruitmentPage'
  | 'RouteDiscussions'
  | 'RouteThemePlayPage'
  | 'RouteEditReview'
  | 'RouteAssignmentBulkImport'
  | 'RouteAssignmentReviewersToPapers'
  | 'RouteAssignmentPapersToReviewers'
  | 'RouteAssignmentsReviewersToReviewers'
  | 'RouteSettingsAnnouncements'
  | 'RouteDashboard'
  | 'RouteKeywords'
  | 'RouteSettingsConference'
  | 'RouteSettingsPaperStatus'
  | 'RouteSettingsPhases'
  | 'RouteReviewerBids'
  | 'RouteAssignmentReviewersToPapersDetail'
  | 'RouteSubmissionBids'
  | 'RouteAssignmentsPapersToReviewersDetail'
  | 'RouteAssignmentsReviewersToReviewersDetail'
  | 'RouteConferenceWizard'
  | 'RoutePersonalInfoWizard'
  | 'RouteUserProfileWizard'
  | 'RouteDataAPIAccess'
  | 'RouteDetailSubmissionReviewer'
  | 'RouteBulkEmailReviewersPage'
  | 'RouteBulkEmailSupervisorsPage'
  | 'RouteDebugPage';

const baseTrackPath = '/conferences/:conference/:track';

export const appRoutes: RouteDefinition[] = [
  {
    name: 'RouteRoot',
    path: '/',
    logged_access: true,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: false,
    track_selected_access: false,
    fragment: <LandingPage />,
  },
  {
    name: 'RouteNotFound',
    path: '/not-found',
    logged_access: true,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: false,
    track_selected_access: false,
    fragment: <NotFoundPage />,
  },
  {
    name: 'RouteConferences',
    path: '/conferences',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: false,
    track_selected_access: false,
    fragment: (
      <LayoutCtrl backgroundColor={DEFAULT_SECONDARY_BACKGROUND_COLOR}>
        <ConferencesPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteConferencesCreate',
    path: '/request-conference',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: false,
    track_selected_access: false,
    fragment: (
      <LayoutCtrl>
        <ConferencesCreatePage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteSettingsConference',
    path: '/manage-conference/:slug',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: false,
    track_selected_access: false,
    fragment: (
      <LayoutCtrl>
        <SettingsConferencePage />
      </LayoutCtrl>
    ),
  },
  {
    name: 'RouteConferenceHome',
    path: baseTrackPath,
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <HomePage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Submissions',
    allow_mobile: true,
  },
  {
    name: 'RoutePeople',
    path: baseTrackPath + '/people',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <PeoplePage />
      </LayoutCtrl>
    ),
    main_menu_option: 'People',
  },
  {
    name: 'RouteProfile',
    path: '/profile',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: false,
    impersonated_access: false,
    track_selected_access: false,
    fragment: (
      <LayoutCtrl>
        <ProfilePage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteSettingsForms',
    path: baseTrackPath + '/settings/forms',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <SettingsFormsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Forms',
  },
  {
    name: 'RouteSettingsRoles',
    path: baseTrackPath + '/settings/roles',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <SettingsRolesPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Roles',
  },
  {
    name: 'RouteSettingsPlanning',
    path: baseTrackPath + '/settings/planning',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <SettingsPlanningPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Planning',
  },
  {
    name: 'RouteAllSubmissions',
    path: baseTrackPath + '/submissions',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_SUBMISSIONS',
    fragment: (
      <LayoutCtrl>
        <AllSubmissionsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Submissions',
  },
  {
    name: 'RouteNewSubmission',
    path: baseTrackPath + '/submissions/create',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'CAN_NEW_SUBMISSION',
    fragment: (
      <LayoutCtrl backgroundColor={DEFAULT_SECONDARY_BACKGROUND_COLOR}>
        <SubmissionNewPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteDetailSubmission',
    path: baseTrackPath + '/submissions/:id',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl backgroundColor={DEFAULT_SECONDARY_BACKGROUND_COLOR}>
        <SubmissionDetailPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteEditSubmission',
    path: baseTrackPath + '/submissions/:id/edit',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl backgroundColor={DEFAULT_SECONDARY_BACKGROUND_COLOR}>
        <SubmissionEditPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteRecruitmentPage',
    path: baseTrackPath + '/recruitment',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_COMMUNICATIONS',
    fragment: (
      <LayoutCtrl>
        <RecruitmentPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Recruitment',
  },
  {
    name: 'RouteEmailTemplatesPage',
    path: baseTrackPath + '/communications' + '/templates',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_COMMUNICATIONS',
    fragment: (
      <LayoutCtrl>
        <EmailTemplatesPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Email templates',
  },
  {
    name: 'RouteEmailHistoryPage',
    path: baseTrackPath + '/communications' + '/history',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_COMMUNICATIONS',
    fragment: (
      <LayoutCtrl>
        <EmailHistoryPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Email history',
  },
  {
    name: 'RouteBulkEmailReviewersPage',
    path: baseTrackPath + '/communications' + '/bulk-email-reviewers',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_COMMUNICATIONS',
    fragment: (
      <LayoutCtrl>
        <BulkEmailReviewersPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Bulk email reviewers',
  },
  {
    name: 'RouteBulkEmailSupervisorsPage',
    path: baseTrackPath + '/communications' + '/bulk-email-supervisors',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_COMMUNICATIONS',
    fragment: (
      <LayoutCtrl>
        <BulkEmailSupervisorsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Bulk email supervisors',
  },
  {
    name: 'RouteDebugPage',
    path: baseTrackPath + '/debug',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <DebugPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Debug',
  },
  {
    name: 'RouteAllReviewersBids',
    path: baseTrackPath + '/bidding/reviewers',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_BIDDING',
    fragment: (
      <LayoutCtrl>
        <AllReviewersBiddingPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Reviewers bids',
  },
  {
    name: 'RouteReviewerBids',
    path: baseTrackPath + '/bidding/reviewers/:id',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_BIDDING',
    fragment: (
      <LayoutCtrl>
        <ReviewerBidsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Reviewers bids',
  },
  {
    name: 'RouteBidding',
    path: baseTrackPath + '/bidding',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_BIDDING',
    fragment: (
      <LayoutCtrl>
        <BiddingPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Bidding',
    allow_mobile: true,
  },
  {
    name: 'RouteAllSubmissionsBids',
    path: baseTrackPath + '/bidding/submissions',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_BIDDING',
    fragment: (
      <LayoutCtrl>
        <AllSubmissionsBiddingPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Submissions bids',
  },
  {
    name: 'RouteSubmissionBids',
    path: baseTrackPath + '/bidding/submissions/:id',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_BIDDING',
    fragment: (
      <LayoutCtrl>
        <SubmissionBidsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Submissions bids',
  },
  {
    name: 'RouteDiscussions',
    path: baseTrackPath + '/submissions/:submissionId/discussion',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <DiscussionPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteLogin',
    path: '/login',
    logged_access: false,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <SignInPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteLogout',
    path: '/logout',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: <LogoutPage />,
  },
  {
    name: 'RouteSignUp',
    path: '/signup',
    logged_access: false,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <SignUpPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteSignUpOAuth',
    path: '/signup-oauth',
    logged_access: false,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <SignUpOAuthPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteForgotPassword',
    path: '/forgot-password',
    logged_access: false,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <ForgotPasswordPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteResetPasswordPage',
    path: '/reset-password/:token',
    logged_access: false,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <ResetPasswordPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteSendEmailVerificationPage',
    path: '/verification-notification',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <SendEmailVerificationPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteAuthCallbackPage',
    path: '/oauth/callback/:provider',
    logged_access: false,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: false,
    impersonated_access: true,
    track_selected_access: false,
    fragment: <AuthCallbackPage />,
  },
  {
    name: 'RouteInvitationFormPage',
    path: '/invitation/:token',
    logged_access: true,
    anonymous_access: true,
    email_verified_access: false,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: false,
    fragment: (
      <AuthLayout>
        <InvitationFormPage />
      </AuthLayout>
    ),
  },
  {
    name: 'RouteThemePlayPage',
    path: '/themeplay',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: false,
    fragment: <ThemePlayPage />,
  },
  {
    name: 'RouteEditReview',
    path: baseTrackPath + '/submissions/:submissionId/reviews/:reviewId/:roleId/edit',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl backgroundColor={DEFAULT_SECONDARY_BACKGROUND_COLOR}>
        <ReviewEditPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteAssignmentBulkImport',
    path: baseTrackPath + '/assignments' + '/bulk-import',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <AssignmentsBulkImportPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Bulk import',
  },
  {
    name: 'RouteAssignmentReviewersToPapers',
    path: baseTrackPath + '/assignments/reviewers-to-papers',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <ReviewersToPapersPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Reviewers to papers',
  },
  {
    name: 'RouteAssignmentReviewersToPapersDetail',
    path: baseTrackPath + '/assignments/reviewers-to-papers/:id',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <ReviewersToPapersDetailPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Reviewers to papers',
  },
  {
    name: 'RouteAssignmentPapersToReviewers',
    path: baseTrackPath + '/assignments/papers-to-reviewers',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <PapersToReviewersPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Papers to reviewers',
  },
  {
    name: 'RouteAssignmentsPapersToReviewersDetail',
    path: baseTrackPath + '/assignments/papers-to-reviewers/:submissionId/:roleId',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <PapersToReviewersDetailPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Papers to reviewers',
  },
  {
    name: 'RouteAssignmentsReviewersToReviewers',
    path: baseTrackPath + '/assignments/reviewers-to-reviewers',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <ReviewersToReviewersPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Reviewers to reviewers',
  },
  {
    name: 'RouteAssignmentsReviewersToReviewersDetail',
    path: baseTrackPath + '/assignments/reviewers-to-reviewers/:id/:roleId',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_ALL_REVIEWS',
    fragment: (
      <LayoutCtrl>
        <ReviewersToReviewersDetailPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Reviewers to reviewers',
  },
  {
    name: 'RouteSettingsAnnouncements',
    path: baseTrackPath + '/settings/announcements',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <SettingsAnnouncementsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Announcements',
  },
  {
    name: 'RouteDataAPIAccess',
    path: baseTrackPath + '/settings/data-api-access',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <DataAPIAccessPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Data & API access',
  },
  {
    name: 'RouteDashboard',
    path: baseTrackPath + '/dashboard/:tab',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <DashboardPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Dashboard',
  },
  {
    name: 'RouteKeywords',
    path: baseTrackPath + '/keywords',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <KeywordsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'User keywords',
    allow_mobile: true,
  },
  // NEW SETTINGS MENU
  {
    name: 'RouteSettingsKeywords',
    path: baseTrackPath + '/settings/keywords',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <SettingsKeywordsPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Keywords',
  },
  {
    name: 'RouteSettingsPaperStatus',
    path: baseTrackPath + '/settings/paper-status',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    permission: 'ACCESS_TRACK_SETTINGS',
    fragment: (
      <LayoutCtrl>
        <SettingsPaperStatusPage />
      </LayoutCtrl>
    ),
    main_menu_option: 'Paper status',
  },
  // WIZARD-LIKE PAGES
  {
    name: 'RouteConferenceWizard',
    path: baseTrackPath + '/wizard',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <ConferenceWizardPage />
      </LayoutCtrl>
    ),
  },
  {
    name: 'RoutePersonalInfoWizard',
    path: '/wizard/personal-info',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: false,
    impersonated_access: false,
    track_selected_access: false,
    fragment: (
      <LayoutCtrl>
        <PersonalInfoWizardPage />
      </LayoutCtrl>
    ),
  },
  {
    name: 'RouteUserProfileWizard',
    path: '/wizard/user-profile',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: false,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl>
        <UserProfileWizardPage />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
  {
    name: 'RouteDetailSubmissionReviewer',
    path: baseTrackPath + '/bidding/submission-detail/:id',
    logged_access: true,
    anonymous_access: false,
    email_verified_access: true,
    personal_info_filled_access: true,
    impersonated_access: true,
    track_selected_access: true,
    fragment: (
      <LayoutCtrl backgroundColor={DEFAULT_SECONDARY_BACKGROUND_COLOR}>
        <SubmissionDetailPageReviewer />
      </LayoutCtrl>
    ),
    allow_mobile: true,
  },
];
